import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  FileText, 
  Plus, 
  Search, 
  X, 
  ExternalLink, 
  Copy, 
  CheckCircle2, 
  Clock, 
  XCircle,
  QrCode,
  Trash2,
  RefreshCw,
  DollarSign,
  Calendar,
  User
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  waitingPayment: { label: 'Aguardando', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  paid: { label: 'Pago', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  canceled: { label: 'Cancelado', color: 'bg-red-100 text-red-700', icon: XCircle },
  processing: { label: 'Processando', color: 'bg-blue-100 text-blue-700', icon: RefreshCw }
};

export default function BoletosPage() {
  const navigate = useNavigate();
  const [boletos, setBoletos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBoleto, setSelectedBoleto] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [searchCliente, setSearchCliente] = useState('');
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [filters, setFilters] = useState({ status: '', mes: '' });
  const [copiedField, setCopiedField] = useState(null);
  
  const [form, setForm] = useState({
    cliente_id: '',
    cliente_nome: '',
    valor: '',
    vencimento: '',
    descricao: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchBoletos = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.mes) params.append('mes', filters.mes);
      
      const [boletosRes, resumoRes] = await Promise.all([
        axios.get(`${API}/api/lytex/boletos?${params}`, { headers }),
        axios.get(`${API}/api/lytex/resumo?${params}`, { headers })
      ]);
      
      setBoletos(boletosRes.data);
      setResumo(resumoRes.data);
    } catch (err) {
      console.error('Erro ao buscar boletos:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, headers]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchBoletos();
  }, [token, navigate, fetchBoletos]);

  useEffect(() => {
    if (searchCliente.length >= 2) {
      axios.get(`${API}/api/clientes/search?q=${searchCliente}`, { headers })
        .then(res => setFilteredClientes(res.data))
        .catch(() => setFilteredClientes([]));
    } else {
      setFilteredClientes([]);
    }
  }, [searchCliente]);

  const handleSelectCliente = (cliente) => {
    setForm(prev => ({
      ...prev,
      cliente_id: cliente.id,
      cliente_nome: cliente.nome
    }));
    setSearchCliente('');
    setFilteredClientes([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.cliente_id || !form.vencimento) {
      alert('Selecione um cliente e data de vencimento');
      return;
    }

    try {
      await axios.post(`${API}/api/lytex/boletos`, {
        cliente_id: form.cliente_id,
        valor: form.valor ? parseFloat(form.valor) : null,
        vencimento: form.vencimento,
        descricao: form.descricao || null
      }, { headers });

      setShowModal(false);
      setForm({ cliente_id: '', cliente_nome: '', valor: '', vencimento: '', descricao: '' });
      fetchBoletos();
    } catch (err) {
      alert('Erro ao criar boleto: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCancel = async (boletoId) => {
    if (!window.confirm('Deseja cancelar este boleto?')) return;
    
    try {
      await axios.delete(`${API}/api/lytex/boletos/${boletoId}`, { headers });
      fetchBoletos();
    } catch (err) {
      alert('Erro ao cancelar boleto: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.waitingPayment;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Boletos</h1>
            <p className="text-slate-500 text-sm mt-1">Gerencie cobranças via boleto e PIX</p>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="new-boleto-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Boleto
          </Button>
        </div>

        {/* KPIs */}
        {resumo && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Emitidos</p>
                  <p className="text-lg font-bold text-slate-800">{resumo.total_emitidos}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Pendentes</p>
                  <p className="text-lg font-bold text-slate-800">{formatCurrency(resumo.valor_pendente)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Recebidos</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(resumo.valor_pago)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Cancelados</p>
                  <p className="text-lg font-bold text-slate-800">{resumo.total_cancelados}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 bg-white p-4 rounded-xl border border-slate-200">
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            data-testid="filter-status"
          >
            <option value="">Todos os Status</option>
            <option value="waitingPayment">Aguardando</option>
            <option value="paid">Pagos</option>
            <option value="canceled">Cancelados</option>
            <option value="processing">Processando</option>
          </select>
          <Input
            type="month"
            value={filters.mes}
            onChange={(e) => setFilters(prev => ({ ...prev, mes: e.target.value }))}
            className="w-auto"
            data-testid="filter-month"
          />
          {(filters.status || filters.mes) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({ status: '', mes: '' })}
              className="text-slate-500"
            >
              <X className="w-4 h-4 mr-1" /> Limpar
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="boletos-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase no-hover">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase no-hover">Valor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase no-hover">Vencimento</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase no-hover">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase no-hover">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Carregando...
                    </td>
                  </tr>
                ) : boletos.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                      Nenhum boleto encontrado
                    </td>
                  </tr>
                ) : boletos.map(boleto => (
                  <tr key={boleto.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-700">{boleto.cliente_nome}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-800">
                      {formatCurrency(boleto.valor)}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {formatDate(boleto.vencimento)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={boleto.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedBoleto(boleto); setShowDetailModal(true); }}
                          title="Ver detalhes"
                          data-testid={`view-boleto-${boleto.id}`}
                        >
                          <QrCode className="w-4 h-4 text-blue-600" />
                        </Button>
                        {boleto.invoice_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(boleto.invoice_url, '_blank')}
                            title="Abrir fatura"
                          >
                            <ExternalLink className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        {boleto.status === 'waitingPayment' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancel(boleto.id)}
                            title="Cancelar"
                            data-testid={`cancel-boleto-${boleto.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal: Novo Boleto */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Novo Boleto</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Cliente Search */}
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">Cliente *</label>
                {form.cliente_nome ? (
                  <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
                    <span className="font-medium">{form.cliente_nome}</span>
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, cliente_id: '', cliente_nome: '' }))}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={searchCliente}
                        onChange={(e) => setSearchCliente(e.target.value)}
                        placeholder="Buscar cliente..."
                        className="pl-9"
                        data-testid="search-cliente-input"
                      />
                    </div>
                    {filteredClientes.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredClientes.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => handleSelectCliente(c)}
                            className="w-full px-4 py-2 text-left hover:bg-slate-100 text-sm"
                          >
                            <div className="font-medium">{c.nome}</div>
                            {c.telefone && <div className="text-xs text-slate-500">{c.telefone}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Valor */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Valor <span className="text-slate-400 text-xs">(deixe vazio para usar valor do plano)</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.valor}
                    onChange={(e) => setForm(prev => ({ ...prev, valor: e.target.value }))}
                    placeholder="0,00"
                    className="pl-9"
                    data-testid="valor-input"
                  />
                </div>
              </div>

              {/* Vencimento */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Vencimento *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="date"
                    value={form.vencimento}
                    onChange={(e) => setForm(prev => ({ ...prev, vencimento: e.target.value }))}
                    className="pl-9"
                    required
                    data-testid="vencimento-input"
                  />
                </div>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
                <Input
                  value={form.descricao}
                  onChange={(e) => setForm(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Ex: Mensalidade Janeiro/2025"
                  data-testid="descricao-input"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" data-testid="submit-boleto-btn">
                  Gerar Boleto
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Detalhes do Boleto */}
      {showDetailModal && selectedBoleto && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-slate-800">Detalhes do Boleto</h2>
              <button onClick={() => setShowDetailModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Cliente</p>
                  <p className="font-medium">{selectedBoleto.cliente_nome}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Valor</p>
                  <p className="font-bold text-lg text-green-600">{formatCurrency(selectedBoleto.valor)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Vencimento</p>
                  <p className="font-medium">{formatDate(selectedBoleto.vencimento)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status</p>
                  <StatusBadge status={selectedBoleto.status} />
                </div>
              </div>

              {/* PIX */}
              {selectedBoleto.pix_codigo && selectedBoleto.status === 'waitingPayment' && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <QrCode className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-slate-800">PIX</h3>
                  </div>
                  {selectedBoleto.pix_qrcode && (
                    <div className="flex justify-center mb-3">
                      <img src={selectedBoleto.pix_qrcode} alt="QR Code PIX" className="w-40 h-40 border rounded-lg" />
                    </div>
                  )}
                  <div className="relative">
                    <Input
                      value={selectedBoleto.pix_codigo}
                      readOnly
                      className="pr-20 text-xs"
                    />
                    <Button
                      size="sm"
                      onClick={() => handleCopy(selectedBoleto.pix_codigo, 'pix')}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7"
                    >
                      {copiedField === 'pix' ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      <span className="ml-1 text-xs">{copiedField === 'pix' ? 'Copiado!' : 'Copiar'}</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* Boleto */}
              {selectedBoleto.boleto_linha_digitavel && selectedBoleto.status === 'waitingPayment' && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-slate-800">Boleto</h3>
                  </div>
                  <div className="relative mb-3">
                    <Input
                      value={selectedBoleto.boleto_linha_digitavel}
                      readOnly
                      className="pr-20 text-xs"
                    />
                    <Button
                      size="sm"
                      onClick={() => handleCopy(selectedBoleto.boleto_linha_digitavel, 'boleto')}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7"
                    >
                      {copiedField === 'boleto' ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      <span className="ml-1 text-xs">{copiedField === 'boleto' ? 'Copiado!' : 'Copiar'}</span>
                    </Button>
                  </div>
                  {selectedBoleto.boleto_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(selectedBoleto.boleto_url, '_blank')}
                      className="w-full"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Visualizar Boleto
                    </Button>
                  )}
                </div>
              )}

              {/* Link da Fatura */}
              {selectedBoleto.invoice_url && (
                <Button
                  onClick={() => window.open(selectedBoleto.invoice_url, '_blank')}
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Abrir Página de Pagamento
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
